import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories, articles } from '@/mocks/home';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState(slug || 'all');

  useEffect(() => {
    if (slug) setActiveCategory(slug);
  }, [slug]);

  const filteredArticles = activeCategory === 'all'
    ? articles
    : articles.filter((a) => a.category === activeCategory);

  const currentCategory = categories.find((c) => c.slug === activeCategory);

  return (
    <main>
      <Navbar />
      <div className="pt-16 md:pt-18">
        <section className="py-12 md:py-16 bg-background-50">
          <div className="w-full px-4 md:px-6 lg:px-10 max-w-7xl mx-auto">
            <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-8" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-primary-600 transition-colors duration-200 cursor-pointer">{t('breadcrumb.home')}</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-600">{t('breadcrumb.category')}</span>
              {currentCategory && (
                <>
                  <i className="ri-arrow-right-s-line text-xs"></i>
                  <span className="text-foreground-800 font-medium">{currentCategory.name}</span>
                </>
              )}
            </nav>

            <div className="text-center mb-10">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                {t('category.title')}
              </h1>
              <p className="text-sm md:text-base text-foreground-500">{t('category.subtitle')}</p>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-2 mb-10">
              <Link
                to="/category/tian-douhua"
                onClick={() => setActiveCategory('all')}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${activeCategory === 'all' ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-600 hover:bg-background-200'}`}
              >
                {t('category.all')}
              </Link>
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => setActiveCategory(cat.slug)}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${activeCategory === cat.slug ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-600 hover:bg-background-200'}`}
                >
                  <i className={`${cat.icon} mr-1.5`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {filteredArticles.map((recipe) => (
                <Link
                  key={recipe.id}
                  to={`/article/${recipe.slug}`}
                  className="group rounded-lg overflow-hidden bg-background-100/70 hover:bg-background-100 transition-all duration-300 cursor-pointer"
                >
                  <div className="relative w-full h-52 overflow-hidden">
                    <img
                      src={recipe.image}
                      alt={recipe.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 left-3">
                      <span className="px-3 py-1 rounded-full text-xs font-medium bg-primary-500 text-background-50">
                        {recipe.categoryName}
                      </span>
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors duration-200">
                      {recipe.title}
                    </h3>
                    <p className="text-xs text-foreground-500 leading-relaxed mb-3 line-clamp-2">
                      {recipe.excerpt}
                    </p>
                    <div className="flex items-center gap-3 text-xs text-foreground-400">
                      <span className="flex items-center gap-1"><i className="ri-tools-line"></i>{recipe.difficulty}</span>
                      <span className="flex items-center gap-1"><i className="ri-time-line"></i>{recipe.prepTime}</span>
                      <span className="flex items-center gap-1"><i className="ri-user-line"></i>{recipe.servings}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {filteredArticles.length === 0 && (
              <div className="text-center py-16">
                <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类下暂无食谱，请查看其他分类</p>
              </div>
            )}
          </div>
        </section>
      </div>
      <Footer />
    </main>
  );
}